// frontend/src/widgets/AuthorityDirectoryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-02
// Updated:      2026-09-30
//
// Description:
//   Authority Directory maintenance widget.
//   - LEFT: Search + select admin areas (geo/admin-areas) with paging (50/page)
//   - RIGHT: View/edit authority_directory row for selected GSS code (authority-directory)
//   - NO wrapper/title/close here: WidgetWrapper handles that (avoid double wrapping).
//
// ✅ UX fix: Left list is scrollable INSIDE a height-constrained panel.
// ✅ Pagination fix: page state drives offset; page changes actually fetch different rows.
// ✅ Safety: resets page to 1 when query/type changes; guards stale responses.
//
// Desktop resize regression fix (2026-09-23):
//   - Mobile host-height overrides are now strictly mobile-only.
//   - Desktop ReactGridLayout height is never captured or restored by this widget.
//   - Replaced direct `.react-grid-item` mutation with the proven width-matching
//     fixed-height host discovery used by the stable mobile list widgets.
//   - Prevents ResizeObserver callbacks from restoring a stale desktop grid height.
//
// Mobile Responsiveness (2026-09-07):
//   - Preserves the existing two-pane desktop maintenance layout at >= 768px.
//   - Mobile stacks the authority browser above the selected directory entry form.
//   - Authority results remain internally scrollable so a 50-row page does not
//     create an excessively long mobile page.
//   - Directory fields collapse from two columns to one and use natural page flow.
//   - Mobile widget host height follows rendered content because the desktop
//     ReactGridLayout resize handle is not available on mobile.
//   - Existing API routes, debounce/stale-response protection, pagination,
//     selection, dirty-state handling, and save behaviour are unchanged.

// 2026-09-30: Added read-only ONS LAD and upper-tier reference above directory
//   fields to support manual verification; no changes to directory persistence.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const AREA_TYPES = [{ value: "LOCAL_AUTH", label: "LOCAL_AUTH" }];

const PAGE_SIZE = 50;

const emptyEntry = (tenantId, gssCode) => ({
  id: null,
  tenant_id: tenantId || null,
  gss_code: gssCode || "",
  council_or_local_authority: "",
  apply_license_url: "",
  licensing_info_url: "",
  highway_authority_url: "",
  permit_scheme_url: "",
  licence_type_1: "",
  licence_type_2: "",
  licence_type_3: "",
  usual_lead_time: "",
  costs_gbp: "",
  previous_costs: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  pca_comments: "",
  other_comments: "",
  is_active: true,
});

const isTruthy = (v) => v === true || v === "true" || v === 1 || v === "1";
const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

const AuthorityDirectoryWidget = () => {
  const { theme } = useTheme();

  const [areaType, setAreaType] = useState("LOCAL_AUTH");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [includeInactiveLookup, setIncludeInactiveLookup] = useState(false);
  const [adminAreas, setAdminAreas] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");

  const [selectedArea, setSelectedArea] = useState(null);

  const [entry, setEntry] = useState(null);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryError, setEntryError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState({ saving: false, ok: false, msg: "" });

  const debounceRef = useRef(null);
  const reqSeqRef = useRef(0);
  const rootRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  const selectedGss = useMemo(() => safeStr(selectedArea?.gss_code), [selectedArea]);

  const accent = theme?.accent_color || "var(--accent-color)";
  const border = "1px solid #e5e5e5";
  const inputStyle = {
    width: "100%",
    minHeight: isMobile ? 44 : undefined,
    padding: "10px 12px",
    borderRadius: 8,
    border,
    outline: "none",
    fontFamily: "var(--font-body)",
    background: "#fff",
    boxSizing: isMobile ? "border-box" : undefined,
  };
  const labelStyle = { fontSize: 13, color: "#444", fontWeight: 600, marginBottom: 6 };

  const fetchAdminAreas = async ({ queryText, type, pageNum }) => {
    const seq = ++reqSeqRef.current;

    setAdminError("");
    setAdminLoading(true);

    const offset = Math.max(0, (Math.max(1, pageNum) - 1) * PAGE_SIZE);

    try {
      const res = await axiosInstance.get("geo/admin-areas", {
        params: {
          area_type: type,
          q: queryText || "",
          limit: PAGE_SIZE,
          offset,
        },
      });

      // stale guard
      if (seq !== reqSeqRef.current) return;

      setAdminAreas(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      console.error("Failed to load admin areas:", e?.response?.data || e?.message);
      setAdminAreas([]);
      setAdminError("Failed to load admin areas.");
    } finally {
      if (seq === reqSeqRef.current) setAdminLoading(false);
    }
  };

  const fetchDirectoryEntryForGss = async (gssCode) => {
    if (!gssCode) {
      setEntry(null);
      setEntryError("");
      setDirty(false);
      return;
    }

    setEntryError("");
    setEntryLoading(true);
    try {
      const res = await axiosInstance.get("authority-directory", {
        params: {
          q: gssCode,
          limit: 20,
          include_inactive: includeInactiveLookup ? "true" : "false",
        },
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      const exact = rows.find((r) => safeStr(r.gss_code).toUpperCase() === gssCode.toUpperCase());

      if (exact) {
        setEntry({ ...emptyEntry(null, gssCode), ...exact, is_active: isTruthy(exact.is_active) });
      } else {
        setEntry(() => {
          const draft = emptyEntry(null, gssCode);
          if (selectedArea?.name) draft.council_or_local_authority = safeStr(selectedArea.name);
          return draft;
        });
      }

      setDirty(false);
      setSaveState({ saving: false, ok: false, msg: "" });
    } catch (e) {
      console.error("Failed to load directory entry:", e?.response?.data || e?.message);
      setEntry(null);
      setEntryError("Failed to load directory entry.");
      setDirty(false);
    } finally {
      setEntryLoading(false);
    }
  };

  const onSelectArea = (area) => {
    if (!area) return;
    setSelectedArea(area);
  };

  // Track the mobile breakpoint without mounting a second widget tree.
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [areaType]);

  useEffect(() => {
    // When q changes, reset page 1 (but do not instantly fetch page 1 twice)
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Fetch admin areas:
  // - debounce only the search text (q)
  // - page changes should fetch immediately (no debounce)
  useEffect(() => {
    // If page changes, fetch immediately.
    // If q changes, debounce.
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const run = () =>
      fetchAdminAreas({
        queryText: q,
        type: areaType,
        pageNum: page,
      });

    // debounce only when q is non-empty OR when user is typing; simplest rule: always debounce on q changes
    debounceRef.current = setTimeout(run, 250);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, areaType, page]);

  useEffect(() => {
    fetchDirectoryEntryForGss(selectedGss);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGss, includeInactiveLookup]);

  const updateField = (field, value) => {
    setEntry((prev) => ({ ...(prev || {}), [field]: value }));
    setDirty(true);
    setSaveState({ saving: false, ok: false, msg: "" });
  };

  const saveEntry = async () => {
    if (!entry) return;

    const gssCode = safeStr(entry.gss_code).trim();
    const name = safeStr(entry.council_or_local_authority).trim();

    if (!gssCode) return setSaveState({ saving: false, ok: false, msg: "Missing GSS code." });
    if (!name) return setSaveState({ saving: false, ok: false, msg: "Council / Local Authority is required." });

    setSaveState({ saving: true, ok: false, msg: "" });
    setEntryError("");

    const payload = {
      gss_code: gssCode,
      council_or_local_authority: name,
      apply_license_url: safeStr(entry.apply_license_url).trim() || null,
      licensing_info_url: safeStr(entry.licensing_info_url).trim() || null,
      highway_authority_url: safeStr(entry.highway_authority_url).trim() || null,
      permit_scheme_url: safeStr(entry.permit_scheme_url).trim() || null,
      licence_type_1: safeStr(entry.licence_type_1).trim() || null,
      licence_type_2: safeStr(entry.licence_type_2).trim() || null,
      licence_type_3: safeStr(entry.licence_type_3).trim() || null,
      usual_lead_time: safeStr(entry.usual_lead_time).trim() || null,
      costs_gbp: safeStr(entry.costs_gbp).trim() || null,
      previous_costs: safeStr(entry.previous_costs).trim() || null,
      contact_name: safeStr(entry.contact_name).trim() || null,
      contact_email: safeStr(entry.contact_email).trim() || null,
      contact_phone: safeStr(entry.contact_phone).trim() || null,
      pca_comments: safeStr(entry.pca_comments).trim() || null,
      other_comments: safeStr(entry.other_comments).trim() || null,
      is_active: isTruthy(entry.is_active),
    };

    try {
      const res = entry.id
        ? await axiosInstance.put(`authority-directory/${entry.id}`, payload)
        : await axiosInstance.post("authority-directory", payload);

      const saved = res?.data || null;
      if (saved) setEntry({ ...emptyEntry(null, gssCode), ...saved, is_active: isTruthy(saved.is_active) });

      setDirty(false);
      setSaveState({ saving: false, ok: true, msg: "Saved." });
    } catch (e) {
      console.error("Save failed:", e?.response?.data || e?.message);
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.response?.data || "Save failed.";
      setSaveState({ saving: false, ok: false, msg: safeStr(msg) });
    }
  };

  const canPrev = page > 1;
  const canNext = adminAreas.length === PAGE_SIZE; // heuristic: if we got a full page, there may be more

  // Mobile pages do not expose the ReactGridLayout resize handle. Let this
  // maintenance widget grow to its rendered content height, but never mutate
  // the desktop ReactGridLayout item. A host is recorded only after the mobile
  // breakpoint has been confirmed and an override is actually about to occur.
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = rootRef.current;
    if (!root) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let observer = null;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + root.scrollHeight + 8
            )
          );

          if (!Number.isFinite(requiredHostHeight) || requiredHostHeight <= 0) {
            restoreHost();
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => applyMobileSizing());
      observer.observe(root);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);
      restoreHost();
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    isMobile,
    selectedGss,
    entryLoading,
    entryError,
    adminLoading,
    adminError,
    adminAreas.length,
    page,
    saveState.msg,
  ]);

  return (
    <div
      ref={rootRef}
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "360px 1fr",
        gap: isMobile ? 12 : 18,
        height: isMobile ? "auto" : "100%",
        minHeight: 0,
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {/* LEFT */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border,
          padding: isMobile ? 12 : 14,
          height: isMobile ? "auto" : "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontWeight: 800, color: "#333" }}>Local Authorities</div>
          <div
            style={{
              border: `2px solid ${accent}`,
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 800,
              color: "#555",
              background: "#fff",
            }}
          >
            {areaType}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Search</div>
          <input
            style={inputStyle}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type e.g. Cherwell or E070..."
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Area type</div>
          <select
            style={{ ...inputStyle, cursor: "pointer" }}
            value={areaType}
            onChange={(e) => setAreaType(e.target.value)}
          >
            {AREA_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {adminError ? <div style={{ color: "#b00020", fontWeight: 700 }}>{adminError}</div> : null}

        {/* ✅ ONLY this section scrolls */}
        <div
          style={{
            marginTop: 10,
            borderTop: border,
            paddingTop: 10,
            overflowY: "auto",
            overscrollBehavior: "contain",
            flex: isMobile ? "0 0 auto" : 1,
            height: isMobile ? 272 : "auto",
            maxHeight: isMobile ? 272 : "none",
            minHeight: 0,
          }}
        >
          {adminLoading ? (
            <div style={{ color: "#666", fontWeight: 600 }}>Loading…</div>
          ) : adminAreas.length === 0 ? (
            <div style={{ color: "#666" }}>No results.</div>
          ) : (
            adminAreas.map((a) => {
              const active =
                safeStr(a.gss_code).toUpperCase() === safeStr(selectedArea?.gss_code).toUpperCase();
              return (
                <button
                  key={`${a.gss_code}-${a.area_type}-${a.name}`}
                  onClick={() => onSelectArea(a)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    background: active ? "rgba(0,0,0,0.03)" : "#fff",
                    border: active ? `1px solid ${accent}` : border,
                    borderRadius: 10,
                    padding: "10px 10px",
                    minHeight: isMobile ? 52 : undefined,
                    marginBottom: 8,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#333" }}>{safeStr(a.name)}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>{safeStr(a.gss_code)}</div>
                </button>
              );
            })
          )}
        </div>

        {/* Pagination footer (outside scroll) */}
        <div
          style={{
            borderTop: border,
            paddingTop: 10,
            marginTop: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <button
            className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white"
            disabled={!canPrev || adminLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "8px 12px",
              minHeight: isMobile ? 44 : undefined,
              flex: isMobile ? "1 1 96px" : undefined,
              borderRadius: 10,
              fontWeight: 900,
              border: "none",
              cursor: !canPrev || adminLoading ? "not-allowed" : "pointer",
              opacity: !canPrev || adminLoading ? 0.6 : 1,
            }}
          >
            Prev
          </button>

          <div style={{ fontWeight: 900, color: "#444" }}>Page {page}</div>

          <button
            className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white"
            disabled={!canNext || adminLoading}
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: "8px 12px",
              minHeight: isMobile ? 44 : undefined,
              flex: isMobile ? "1 1 96px" : undefined,
              borderRadius: 10,
              fontWeight: 900,
              border: "none",
              cursor: !canNext || adminLoading ? "not-allowed" : "pointer",
              opacity: !canNext || adminLoading ? 0.6 : 1,
            }}
          >
            Next
          </button>
        </div>
      </div>

      {/* RIGHT */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border,
          padding: isMobile ? 12 : 16,
          height: isMobile ? "auto" : "100%",
          minHeight: 0,
          overflow: isMobile ? "visible" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: "#333" }}>Directory Entry</div>
            <div style={{ color: "#666", marginTop: 2 }}>
              {selectedGss ? "Edit details for the selected Local Authority." : "Select a Local Authority on the left."}
            </div>
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minHeight: isMobile ? 44 : undefined,
              fontWeight: 700,
              color: "#555",
            }}
          >
            <input
              type="checkbox"
              checked={includeInactiveLookup}
              onChange={(e) => setIncludeInactiveLookup(e.target.checked)}
            />
            Include inactive lookup
          </label>
        </div>

        <div
          style={{
            borderTop: border,
            marginTop: 12,
            paddingTop: 12,
            flex: isMobile ? "0 0 auto" : 1,
            minHeight: 0,
          }}
        >
          {entryLoading ? (
            <div style={{ color: "#666", fontWeight: 700 }}>Loading…</div>
          ) : entryError ? (
            <div style={{ color: "#b00020", fontWeight: 800 }}>{entryError}</div>
          ) : !entry ? (
            <div style={{ color: "#666" }}>No selection.</div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "1fr 1fr",
                gap: isMobile ? 12 : 14,
                overflowY: isMobile ? "visible" : "auto",
                height: isMobile ? "auto" : "100%",
                paddingRight: isMobile ? 0 : 6,
                minHeight: 0,
              }}
            >
              <div
                style={{
                  gridColumn: isMobile ? "1" : "1 / span 2",
                  border,
                  borderRadius: 8,
                  padding: 12,
                  background: "#f7f7f7",
                }}
              >
                <div style={{ ...labelStyle, marginBottom: 10 }}>ONS Authority Reference (read-only)</div>
                <div style={{ fontSize: 13, color: "#444", marginBottom: 6 }}>
                  <strong>Local Authority:</strong> {safeStr(selectedArea?.name) || "—"}
                  {selectedArea?.gss_code ? ` (${selectedArea.gss_code})` : ""}
                </div>
                <div style={{ fontSize: 13, color: "#444" }}>
                  <strong>{selectedArea?.has_separate_upper_tier ? "Upper-tier County:" : "County / Unitary:"}</strong>{" "}
                  {selectedArea?.ctyua_gss_code
                    ? `${safeStr(selectedArea.ctyua_name) || "—"} (${selectedArea.ctyua_gss_code})`
                    : "No ONS hierarchy match"}
                  {selectedArea?.ctyua_gss_code && !selectedArea?.has_separate_upper_tier
                    ? " — single-tier / self-mapped authority"
                    : ""}
                </div>
              </div>
              <div>
                <div style={labelStyle}>GSS Code (read-only)</div>
                <input style={{ ...inputStyle, background: "#f7f7f7" }} value={safeStr(entry.gss_code)} readOnly />
              </div>

              <div>
                <div style={labelStyle}>Active</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      borderRadius: 999,
                      padding: "6px 12px",
                      fontWeight: 900,
                      border: `2px solid ${entry.is_active ? "#1a9c5a" : "#999"}`,
                      color: entry.is_active ? "#1a9c5a" : "#777",
                      background: "#fff",
                    }}
                  >
                    {entry.is_active ? "ACTIVE" : "INACTIVE"}
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#555" }}>
                    <input
                      type="checkbox"
                      checked={!!entry.is_active}
                      onChange={(e) => updateField("is_active", e.target.checked)}
                    />
                    Toggle
                  </label>
                </div>
              </div>

              <div style={{ gridColumn: isMobile ? "1" : "1 / span 2" }}>
                <div style={labelStyle}>Council / Local Authority (required)</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.council_or_local_authority)}
                  onChange={(e) => updateField("council_or_local_authority", e.target.value)}
                  placeholder="e.g. Cherwell District Council"
                />
              </div>

              <div>
                <div style={labelStyle}>Link to apply for licence(s)</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.apply_license_url)}
                  onChange={(e) => updateField("apply_license_url", e.target.value)}
                  placeholder="https://…"
                />
              </div>

              <div>
                <div style={labelStyle}>Licensing info URL</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.licensing_info_url)}
                  onChange={(e) => updateField("licensing_info_url", e.target.value)}
                  placeholder="https://…"
                />
              </div>

              <div>
                <div style={labelStyle}>Highway authority URL</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.highway_authority_url)}
                  onChange={(e) => updateField("highway_authority_url", e.target.value)}
                  placeholder="https://…"
                />
              </div>

              <div>
                <div style={labelStyle}>Permit scheme URL</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.permit_scheme_url)}
                  onChange={(e) => updateField("permit_scheme_url", e.target.value)}
                  placeholder="https://…"
                />
              </div>

              <div>
                <div style={labelStyle}>Licence type 1</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.licence_type_1)}
                  onChange={(e) => updateField("licence_type_1", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Licence type 2</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.licence_type_2)}
                  onChange={(e) => updateField("licence_type_2", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Licence type 3</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.licence_type_3)}
                  onChange={(e) => updateField("licence_type_3", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Usual lead time</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.usual_lead_time)}
                  onChange={(e) => updateField("usual_lead_time", e.target.value)}
                  placeholder="e.g. 10 working days"
                />
              </div>

              <div>
                <div style={labelStyle}>Costs £ (if known)</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.costs_gbp)}
                  onChange={(e) => updateField("costs_gbp", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Previous costs</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.previous_costs)}
                  onChange={(e) => updateField("previous_costs", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Contact name</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.contact_name)}
                  onChange={(e) => updateField("contact_name", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Contact email</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.contact_email)}
                  onChange={(e) => updateField("contact_email", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div>
                <div style={labelStyle}>Phone</div>
                <input
                  style={inputStyle}
                  value={safeStr(entry.contact_phone)}
                  onChange={(e) => updateField("contact_phone", e.target.value)}
                  placeholder="Text"
                />
              </div>

              <div />

              <div style={{ gridColumn: isMobile ? "1" : "1 / span 2" }}>
                <div style={labelStyle}>PCA Comments</div>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                  value={safeStr(entry.pca_comments)}
                  onChange={(e) => updateField("pca_comments", e.target.value)}
                  placeholder="Internal notes…"
                />
              </div>

              <div style={{ gridColumn: isMobile ? "1" : "1 / span 2" }}>
                <div style={labelStyle}>Other Comments</div>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                  value={safeStr(entry.other_comments)}
                  onChange={(e) => updateField("other_comments", e.target.value)}
                  placeholder="Anything else…"
                />
              </div>

              <div
                style={{
                  gridColumn: isMobile ? "1" : "1 / span 2",
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "stretch" : "center",
                  gap: 12,
                  paddingBottom: 6,
                }}
              >
                <button
                  className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
                  onClick={saveEntry}
                  disabled={saveState.saving || !dirty}
                  style={{
                    padding: "10px 16px",
                    minHeight: isMobile ? 44 : undefined,
                    width: isMobile ? "100%" : "auto",
                    borderRadius: 10,
                    fontWeight: 900,
                    border: "none",
                    cursor: saveState.saving || !dirty ? "not-allowed" : "pointer",
                    opacity: saveState.saving || !dirty ? 0.65 : 1,
                  }}
                >
                  {saveState.saving ? "Saving…" : "Save"}
                </button>

                {saveState.msg ? (
                  <div style={{ fontWeight: 800, color: saveState.ok ? "#1a9c5a" : "#b00020" }}>{saveState.msg}</div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorityDirectoryWidget;